import { Card, CardContent } from "@/components/ui/card";
import { TodayData, DashboardFilters } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { Zap, ShoppingBag, Target } from "lucide-react";

interface TodayCardProps {
  data: TodayData;
  period?: DashboardFilters["period"];
  goalType?: "meta" | "super_meta";
  requiredDailyAverage?: number;
}

const periodLabels: Record<string, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  week: "Semana",
  month: "Mês",
  custom: "Período",
};

export function TodayCard({ data, period = "today", goalType = "meta", requiredDailyAverage = 0 }: TodayCardProps) {
  const periodLabel = periodLabels[period || "today"] || "Hoje";
  const dailyGoal = requiredDailyAverage > 0 ? requiredDailyAverage : data.daily_goal;
  const progress = dailyGoal > 0 ? (data.sales_total / dailyGoal) * 100 : 0;
  const progressClamped = Math.min(progress, 100);
  const goalLabel = goalType === "super_meta" ? "Super Meta" : "Meta";
  const remaining = Math.max(0, dailyGoal - data.sales_total);

  return (
    <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 text-white">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
              <Zap className="h-4 w-4" />
            </div>
            <span className="font-medium">Vendas - {periodLabel}</span>
          </div>
        </div>

        {/* Main Value */}
        <div className="mb-5">
          <p className="text-blue-200 text-xs mb-1">Total vendido</p>
          <p className="text-xl font-bold truncate">{Mask.formatCurrency(data.sales_total)}</p>
        </div>

        {/* Goal Progress */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-4">
          <div className="flex items-center gap-1 mb-2">
            <Target className="h-3 w-3 text-blue-200 flex-shrink-0" />
            <span className="text-xs text-blue-100 truncate">{goalLabel}: {Mask.formatCurrency(dailyGoal)}</span>
            <span className="ml-auto text-xs font-bold flex-shrink-0">{progress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 mb-2">
            <div
              className="h-full rounded-full bg-white transition-all duration-500"
              style={{ width: `${progressClamped}%` }}
            />
          </div>
          <p className="text-xs text-blue-200 truncate">
            Faltam <span className="font-semibold text-white">{Mask.formatCurrency(remaining)}</span>
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 pt-3 border-t border-white/20">
          <ShoppingBag className="h-4 w-4 text-blue-200" />
          <span className="text-sm text-blue-100">
            <span className="font-semibold text-white">{data.order_count}</span> pedidos
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
